import { useEffect, useReducer, useRef, type FormEvent } from 'react';
import { useUser } from '@clerk/clerk-react';
import { ArrowRightIcon, CheckIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { AppHeaderConfig } from '@/contexts/AppHeaderContext';
import { useCreateSpace } from '@/hooks/queries/useSpaceInvitations';
import { ApiError } from '@/lib/api';
import { useRouter } from '@/lib/router';
import { initialSpaceDraft, spaceCreatedEvents, spaceCreationRequest, spaceDraftReducer, spaceInviteErrors, validateSpaceDraft } from '@/lib/spaces/creation';
import { spaceHref } from '@/lib/spaces/navigation';
import { track } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import { SpaceRulesHint, SpaceTypeStep } from './SpaceFormParts';
import { SpacePeopleStep } from './SpacePeopleStep';
import { SpacePrivacyAside } from './SpacePrivacyAside';

const TITLES = ['Que tipo de espaço?', 'Pessoas e percentuais'];
const STEPS = ['Tipo e nome', 'Pessoas e percentuais'];

export function CreateSpacePage() {
  const { user } = useUser();
  const router = useRouter();
  const [draft, dispatch] = useReducer(spaceDraftReducer, undefined, initialSpaceDraft);
  const { trigger, isMutating } = useCreateSpace();
  const heading = useRef<HTMLHeadingElement>(null);
  const creatorEmails = user?.emailAddresses.map((address) => address.emailAddress) ?? [];
  const isFinal = draft.step === 2;
  useEffect(() => { heading.current?.focus(); }, [draft.step]);

  const handleBack = () => dispatch({ type: 'step', step: 1 });
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isMutating) return;
    const errors = validateSpaceDraft(draft, draft.step, creatorEmails);
    if (Object.keys(errors).length) { dispatch({ type: 'errors', errors }); return; }
    if (!isFinal) { dispatch({ type: 'step', step: 2 }); return; }
    for (const step of [1, 2] as const) {
      const priorErrors = validateSpaceDraft(draft, step, creatorEmails);
      if (Object.keys(priorErrors).length) { dispatch({ type: 'errors', errors: priorErrors, step }); return; }
    }
    try {
      const space = await trigger(spaceCreationRequest(draft));
      const events = spaceCreatedEvents(space, draft);
      track('space.created', events.created);
      for (const invite of events.invites) track('space.invite.sent', invite);
      toast.success(draft.people.length > 1 ? 'Espaço criado. Os convites estão disponíveis na Vértice.' : 'Espaço criado.');
      router.push(spaceHref(space.id));
    } catch (error) {
      const message = error instanceof ApiError && [400, 403, 409].includes(error.status) ? error.message
        : 'Não foi possível criar o espaço. Verifique sua conexão antes de tentar novamente.';
      const fields = spaceInviteErrors(draft.people, message);
      dispatch({ type: 'errors', errors: fields, message, step: Object.keys(fields).length ? 2 : draft.step });
      toast.error(message);
    }
  };

  const renderStep = (label: string, index: number) => <li key={label} aria-current={draft.step === index + 1 ? 'step' : undefined} className="flex min-w-0 flex-1 flex-col items-start gap-2 text-xs sm:flex-row sm:items-center">
          <span className={cn('grid size-6 shrink-0 place-items-center rounded-full font-bold', draft.step >= index + 1 ? 'bg-primary text-primary-foreground' : 'bg-surface-sunken text-text-secondary')}>{draft.step > index + 1 ? <CheckIcon aria-hidden className="size-3" /> : index + 1}</span><span className={draft.step >= index + 1 ? 'font-medium text-text-primary' : 'text-text-secondary'}>{label}</span>
        </li>;

  return <section className="space-y-6">
    <AppHeaderConfig hidePeriodPicker />
    <div><h1 className="text-2xl font-bold tracking-tight text-text-primary">Criar um espaço</h1><p className="mt-1 text-sm text-text-secondary">Gerencie o que é de todos, sem precisar compartilhar tudo.</p></div>
    <form onSubmit={handleSubmit} noValidate aria-busy={isMutating} className="mx-auto w-full max-w-5xl rounded-3xl border border-border-subtle bg-surface p-5 shadow-sm sm:p-8">
      <ol aria-label="Etapas de criação" className="mb-7 flex gap-3">
        {STEPS.map(renderStep)}
      </ol>
      <div className="grid items-start gap-6 lg:grid-cols-3">
        <fieldset disabled={isMutating} className="min-w-0 space-y-5 lg:col-span-2">
          <legend className="sr-only">{STEPS[draft.step - 1]}</legend>
          <h2 ref={heading} tabIndex={-1} className="text-lg font-bold text-text-primary outline-none">{TITLES[draft.step - 1]}</h2>
          <SpaceTypeStep draft={draft} dispatch={dispatch} />
          <SpacePeopleStep draft={draft} dispatch={dispatch} creatorName={user?.fullName ?? 'Você'} creatorEmails={creatorEmails} />
          <SpaceRulesHint visible={isFinal} />
          <CreationError message={draft.submitError} />
          <div className="flex flex-wrap items-center gap-3">
            <BackButton visible={draft.step > 1} onBack={handleBack} />
            <Button type="submit" disabled={isMutating} className="max-w-full whitespace-normal">{isFinal ? <CheckIcon aria-hidden className="size-4 shrink-0" /> : <ArrowRightIcon aria-hidden className="size-4 shrink-0" />}{isMutating ? 'Criando espaço…' : isFinal ? 'Criar espaço' : 'Continuar'}</Button>
          </div>
        </fieldset>
        <SpacePrivacyAside />
      </div>
    </form>
  </section>;
}

function BackButton({ visible, onBack }: { visible: boolean; onBack: () => void }) {
  if (!visible) return null;
  return <Button type="button" variant="ghost" onClick={onBack}>Voltar</Button>;
}

function CreationError({ message }: { message: string | null }) {
  if (!message) return null;
  return <p role="alert" className="rounded-xl border border-error-border bg-error-surface p-3 text-sm text-error-text">{message}</p>;
}
